import { useEffect } from 'react';

import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useChatDeleteMutation } from '@school_ify/schoolify-repository/src/teacher/types';

import socket from 'domain/services/socketIoTemp';
import RenderWithRole from 'hoc/renderWithRole';
import { addActiveRoom, cleanChatRoom } from 'redux/chat/actions';
import {
  fetchGroupChats,
  markAllMassagesAsRead,
  selectGroupChats,
} from 'redux/chat/group_messages.slices';
import { getActiveChat, isInitialChat } from 'redux/chat/selectors';
import { useAppSelector } from 'redux/hooks';
import {
  changeCurrentScreen,
  getCurrentScreenView,
} from 'redux/mobile_screen_flow/mobile_screen_flow.slice';
import { selectDirectChats } from 'redux/chat/direct_messages.slices';
import useDeviceDetect from 'hooks/useDeviceDetect';
import useQuery from 'hooks/useQuery';

import CreateChat from '../CreateChat/CreateChat';

import './GroupMessages.scoped.scss';
import Delete from 'components/Delete/Delete';
import { getUser } from 'redux/authentication/auth.slice';

interface Props {
  children?: React.ReactNode;
}

const GroupMessages: React.FC<Props> = () => {
  const user = useAppSelector(getUser);
  const dispatch = useDispatch();
  const activeChat = useAppSelector(getActiveChat);
  const initialChat = useAppSelector(isInitialChat);
  const { groupChats, loading } = useAppSelector(selectGroupChats);
  const { loading: directLoading, participants } = useAppSelector(selectDirectChats);

  const history = useHistory();
  const query = useQuery();

  useEffect(() => {
    dispatch(fetchGroupChats());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //   On chage of groupchat add first chat room as active

  const selectRoom = (room, isDirect = false) => {
    if (activeChat) {
      socket.emit('leaveChat', activeChat.id);
      dispatch(cleanChatRoom());
    }

    socket.emit('joinChat', room.id);
    let fixObj;
    if (isDirect) {
      fixObj = {
        __typename: 'chats',
        avatar_url: '',
        participants: [],
        id: room.id,
        title: room.user?.full_name,
      };
    }
    dispatch(markAllMassagesAsRead(room.id));
    dispatch(addActiveRoom(fixObj ?? room));
    history.push({ pathname: '/chat', search: `id=${room.id}` });
  };

  // Check if active chat is equal to the some of the existing group or private chat and join that room,else join first room

  useEffect(() => {
    if (
      loading === 'success' &&
      directLoading === 'success' &&
      groupChats.length > 0 &&
      !initialChat
    ) {
      const chatId = query.get('id');

      const selectedGroup = groupChats.find(groupChat => groupChat.id === chatId);
      const selectedGroupByActiveChat = groupChats.find(
        groupChat => groupChat.id === activeChat?.id
      );
      const selectedDirect = participants.find(direct => direct.id === chatId);

      if (selectedGroup) {
        selectRoom(selectedGroup);
      } else if (selectedDirect) {
        selectRoom(selectedDirect, true);
      } else if (selectedGroupByActiveChat) {
        selectRoom(selectedGroupByActiveChat);
      } else {
        selectRoom(groupChats[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, directLoading]);

  const { isMobile } = useDeviceDetect();

  const goToChat = groupChat => {
    if (activeChat.id !== groupChat.id) {
      selectRoom(groupChat);
      isMobile && dispatch(changeCurrentScreen('chat'));
    }
  };

  const CreateChatOption = RenderWithRole({
    teacher: <CreateChat />,
    // student: <CreateChat />
  });
  const currentScreenView = useAppSelector(getCurrentScreenView);

  const [deleteChatMutation] = useChatDeleteMutation();

  const deleteMe = id => {
    deleteChatMutation({
      variables: {
        id,
      },
      onCompleted() {
        dispatch(fetchGroupChats());
      },
    });
  };

  return (
    <>
      <div className="subject-titles">
        {groupChats &&
          groupChats.map(groupChat => (
            <div
              className={classNames('item pointer', {
                active: activeChat && activeChat.id === groupChat.id,
              })}
              onClick={() => goToChat(groupChat)}
              key={groupChat.id}
            >
              <h4 key={groupChat.title}># {groupChat.title}</h4>
              <div className="filler"></div>
              {groupChat.unread_messages > 0 && (
                <div className="unread-messages">
                  <span>{groupChat.unread_messages}</span>
                </div>
              )}
              {user.role !== 'student' && (
                <div className="close-icon">
                  <Delete confirmed={() => deleteMe(groupChat.id)} description={groupChat.title} />
                </div>
              )}
            </div>
          ))}
      </div>

      {isMobile && currentScreenView === 'list' && CreateChatOption}
      {!isMobile && CreateChatOption}
    </>
  );
};

export default GroupMessages;
